import {createSelector} from 'reselect';
import {
  getIssueTypeById,
  getIssueTypeForEdit,
  getSelectedIssueTypeAttributeDefinitionIds,
  getSelectedIssueTypeAttributes
} from './issueTypes-selectors';
import {isInheritedAttribute} from '../../utils/issueAttributes-helpers';
import {sortAlphabeticallyByPath} from '../../utils/sortingHelpers';

export const getIssueAttributesDefinitionsEntitiesMap = state => state.issueAttributes.definitions.entitiesMap;
export const getIssueAttributesDefinitionIds = state => state.issueAttributes.definitions.issueAttributeIds;
export const isErrorLoadingIssueAttributesDefinitions = state => state.issueAttributes.definitions.error;
export const isLoadingIssueAttributesDefinitions = state => state.issueAttributes.definitions.loading;
export const isCreatingIssueAttributesDefinition = state => state.issueAttributes.definitions.isCreating;
export const isUpdatingIssueAttributesDefinition = state => state.issueAttributes.definitions.isUpdating;
export const getIssueAttributesDefinitionsTableHighlightedRows = state =>
  state.issueAttributes.definitions.highlightRowIds;
export const getIssueAttributesDefinitionTypesCount = state => state.issueAttributes.definitions.definitionTypesCount;
export const isIssueAttributesDefinitionsFetched = state => state.issueAttributes.definitions.fetched;
export const getIssueAttributesDefinitionEditingId = state => state.issueAttributes.definitions.editingId;
export const isDeletingIssueAttributesDefinition = state => state.issueAttributes.definitions.isDeleting;
export const isIssueAttributeDefinitionsModalOpen = state => state.issueAttributes.definitions.modalOpen;

export const getIssueAttributesMappingEntitiesMap = state => state.issueAttributes.mappings.entitiesMap;
export const getIssueAttributesMappingIds = state => state.issueAttributes.mappings.mappingIds;
export const isErrorLoadingIssueAttributesMappings = state => state.issueAttributes.mappings.error;
export const isLoadingIssueAttributesMappings = state => state.issueAttributes.mappings.loading;
export const isUpdatingIssueAttributesMappings = state => state.issueAttributes.mappings.isUpdating;
export const isIssueAttributesMappingsFetched = state => state.issueAttributes.mappings.fetched;

export const getIssueAttributesDefinitionsMappedEntities = createSelector(
  getIssueAttributesDefinitionsEntitiesMap,
  getIssueAttributesDefinitionIds,
  (entitiesMap, issueAttributeIds) => issueAttributeIds.map(issueAttributeId => entitiesMap[issueAttributeId])
);

export const getIssueAttributesMappingsMappedEntities = createSelector(
  getIssueAttributesMappingEntitiesMap,
  getIssueAttributesMappingIds,
  (entitiesMap, mappingIds) => mappingIds.map(mappingId => entitiesMap[mappingId])
);

export const getCurrentEditingIssueAttribute = createSelector(
  getIssueAttributesDefinitionsEntitiesMap,
  getIssueAttributesDefinitionEditingId,
  (entitiesMap, id) => entitiesMap[id]
);

export const getCurrentEditingIssueTypeAttributeMappings = createSelector(
  getIssueAttributesMappingEntitiesMap,
  getIssueTypeForEdit,
  (entitiesMap, issueType) => issueType.mappingIds.map(attribute => entitiesMap[attribute])
);

export const getInheritedIssueTypeAttributeMappings = createSelector(
  getIssueAttributesDefinitionsEntitiesMap,
  getCurrentEditingIssueTypeAttributeMappings,
  getIssueTypeForEdit,
  (definitionsMap, attributeMappings, issueType) =>
    attributeMappings.filter(mapping => isInheritedAttribute(mapping, !!issueType.parentId, issueType.isContainerType))
);

export const getInheritedIssueTypeAttributeDefinitionIds = createSelector(
  getInheritedIssueTypeAttributeMappings,
  inheritedIssueTypeAttributeMappings =>
    inheritedIssueTypeAttributeMappings.map(mapping => mapping.attributeDefinitionId)
);

// Used to get all inherited & selected attribute mappings to be displayed in edit issue type modal
export const getIssueTypeDisplayAttributeMappings = createSelector(
  getInheritedIssueTypeAttributeMappings,
  getSelectedIssueTypeAttributes,
  (inheritedMappings, selectedIssueTypeMappings) =>
    [...inheritedMappings, ...selectedIssueTypeMappings].filter(mapping => mapping)
);

export const getIssueTypeDisplayAttributeDefinitions = createSelector(
  getIssueAttributesDefinitionsEntitiesMap,
  getIssueTypeDisplayAttributeMappings,
  (definitionsEntitiesMap, issueTypeDisplayAttributeMappings) =>
    issueTypeDisplayAttributeMappings
      .map(mapping => definitionsEntitiesMap[mapping.attributeDefinitionId])
      .filter(definition => definition)
);

// Used to get all mappings which are not inherited, used on issue type edit
export const getFilteredIssueTypeAttributeMappingsByIssueTypeId = createSelector(
  getIssueAttributesMappingEntitiesMap,
  (state, issueTypeId) => getIssueTypeById(state, issueTypeId),
  (entitiesMap, issueType) =>
    issueType.mappingIds
      .map(mappingId => entitiesMap[mappingId])
      .filter(mapping => !isInheritedAttribute(mapping, !!issueType.parentId, issueType.isContainerType))
);

// Used to get selectable issue attributes (without inherited and currently selected) on issue type add attributes modal
export const getFilteredIssueTypeAttributeDefinitions = createSelector(
  getIssueAttributesDefinitionsEntitiesMap,
  getIssueAttributesDefinitionIds,
  getSelectedIssueTypeAttributeDefinitionIds,
  getInheritedIssueTypeAttributeDefinitionIds,
  (definitionsEntitiesMap, definitionIds, selectedIssueTypeDefinitionIds, inheritedDefinitionIds) =>
    definitionIds
      .filter(
        definitionId =>
          !selectedIssueTypeDefinitionIds.includes(definitionId) && !inheritedDefinitionIds.includes(definitionId)
      )
      .map(definitionId => definitionsEntitiesMap[definitionId])
      .asMutable()
);

export const getIssueAttributeDefinitionsWhichAreMappedToIssueTypes = createSelector(
  getIssueAttributesMappingsMappedEntities,
  getIssueAttributesDefinitionsEntitiesMap,
  (mappings, definitions) => {
    const mappedDefinitions = [];
    const mappedDefinitionIds = [];
    mappings.forEach(mapping => {
      const definition = definitions[mapping.attributeDefinitionId];
      if (definition && !mappedDefinitionIds.includes(mapping.attributeDefinitionId)) {
        mappedDefinitionIds.push(mapping.attributeDefinitionId);
        mappedDefinitions.push(definitions[mapping.attributeDefinitionId]);
      }
    });
    return mappedDefinitions;
  }
);

export const getSortedIssueAttributeDefinitionsWhichAreMappedToIssueTypes = createSelector(
  getIssueAttributeDefinitionsWhichAreMappedToIssueTypes,
  mappedDefinitions => sortAlphabeticallyByPath(mappedDefinitions, 'title')
);
